import React, {FC, useEffect, useState} from 'react';
import {Descriptions, Row, Col, Button} from 'antd';
import { useLang } from '@/hooks/useLang';
import {AntModal, DateTimeUtils, Message} from "@/utils";
import {DepartmentHeadRecommendationApi} from "@/api";
import SendBackActionController from "@components/UserAction/SendBackAction/SendBackAction.controller";
import SpecialResearchGrantPreviewLoader
    from "@/modules/UserPanel/components/Grant/Application/SpecialResearchGrant/Content/SpecialResearchGrant.preview.loader";
import {useErrorHandler} from "@/hooks/useErrorHandler";

const DepartmentHeadRecommendationView: FC<any> = props => {
    const {itemData, handleCallbackFunc} = props;
    const DescriptionsItem = Descriptions.Item;
    const { t } = useLang();
    const { handleErrorMessage, handleSuccessMessage, showErrorMessage } = useErrorHandler();

    const [piResearcherProfileData, setPiResearcherProfileData] = useState<any>({});
    const [aiResearcherProfileData, setAiResearcherProfileData] = useState<any>({});

    useEffect(() => {
        if (itemData.id) {
            setPiResearcherProfileData(itemData?.pi_application_profile);
            setAiResearcherProfileData(itemData?.ai_application_profile);
        }
    }, [itemData.id]);

    const handleActions = (action: string, recordId?: any) => {
        if (action === 'RECOMMEND') {
            AntModal.confirm(t('Recommend'), t('Are you sure you want to recommend?'), recordId, handleActionRecommended, 'Approve');
        }
        else if (action === 'REJECTED') {
            AntModal.confirm(t('Reject'), t('Are you sure you want to reject?'), recordId, handleActionReject, 'Reject');
        }
    };

    const handleActionRecommended = (applicationId:any, action: any) => {
        if (applicationId && action === 'ok') {
            const payload = {
                'status': 'SUBMITTED',
                'application_id': applicationId,
                'institute_organization_id': itemData.institute_organization_id,
            }
            DepartmentHeadRecommendationApi.departmentHeadRecommend(payload)
              .then(res => {
                  handleCallbackFunc(null, "reloadView");
                  handleCallbackFunc(null, "reloadListing");
              })
              .catch(err => {
                  handleErrorMessage(err);
              });
        }
    }

    const handleActionReject = (applicationId: any, action: any) => {
        if (applicationId && action === 'ok') {
            const payload = {
                'status': 'NOT_RECOMMENDED'
            }
            DepartmentHeadRecommendationApi.updatePartial(applicationId, payload)
              .then(res => {
                  handleCallbackFunc(null, "reloadView");
                  handleCallbackFunc(null, "reloadListing");
              })
              .catch(err => {
                  handleErrorMessage(err);
              });
        }
    }

    const handleActionSendBack = (data) => {
        const payload = {
            ...data,
            id: itemData.id,
            pi_researcher_profile_id: itemData.pi_researcher_profile_id,
            ai_researcher_profile_id: itemData.ai_researcher_profile_id,
            created_by: itemData.created_by,
            status: itemData.status,
        }
        DepartmentHeadRecommendationApi.sendBackApplication(payload);

        const updatePayload = {
            'status': 'DRAFT',
            'current_active_step' : 5,
        }
        DepartmentHeadRecommendationApi.updatePartial(itemData.id, updatePayload)
          .then(res => {
              handleCallbackFunc(null, "reloadView");
              handleCallbackFunc(null, "reloadListing");
          })
          .catch(err => {
              handleErrorMessage(err);
        });
    }
    
    return (
        <div className="view-page-content view-page-content-nst-fellowship">
            <div className='record-detail-content rounded'>
                <Row gutter={[16, 16]}>
                    {itemData.status === 'RECOMMENDATION_PENDING' && (
                    <Col xs={24} md={24}>
                        <div className="actions">
                            <Button
                                type="primary"
                                className="btn btn-submit btn-transition"
                                onClick={(event) => handleActions('RECOMMEND', itemData.id)}
                            >
                                {'Recommend'}
                            </Button>
                            <Button
                                type="primary"
                                className="btn btn-submit btn-transition"
                                style={{marginLeft: 10}}
                                onClick={(event) => handleActions('REJECTED', itemData.id)}
                            >
                                {'Reject'}
                            </Button>

                            &nbsp;&nbsp;&nbsp;

                            <SendBackActionController
                              applicationData={itemData}
                              handleActionSendBack={handleActionSendBack}
                              handleCallbackFunc={handleCallbackFunc}
                            />
                        </div>
                    </Col>
                    )}
                    <Col xs={24} md={24}>
                        <div className='record-detail-content-left'>
                            <h3 className='fw-bold blue-color-text'>Project Information: </h3>
                            <Descriptions
                                bordered
                                column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                            >
                                <DescriptionsItem label={t("Project Title")}>{itemData.project_title}</DescriptionsItem>
                                <DescriptionsItem label={t("Area of Research")}>{itemData.research_areas_en}</DescriptionsItem>
                                <DescriptionsItem label={t("Institute/Organization")}>{itemData.institute_name}</DescriptionsItem>
                                <DescriptionsItem label={t("Department")}>{itemData.research_performed_department}</DescriptionsItem>
                            </Descriptions>

                            <h3 className='fw-bold blue-color-text'>Principal Investigator (PI): </h3>
                            <Descriptions
                                bordered
                                column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                            >
                                <DescriptionsItem label={t("Name")}>{piResearcherProfileData?.name_en}</DescriptionsItem>
                                <DescriptionsItem label={t("Gender")}>{piResearcherProfileData?.gender}</DescriptionsItem>
                                <DescriptionsItem label={t("Date of Birth")}>{DateTimeUtils.formatDate(piResearcherProfileData?.dob)}</DescriptionsItem>
                                <DescriptionsItem label={t("Position")}>{piResearcherProfileData?.position_en}</DescriptionsItem>
                                <DescriptionsItem label={t("Position held (since)")}>{piResearcherProfileData?.position_held}</DescriptionsItem>
                                <DescriptionsItem label={t("E-mail")}>{piResearcherProfileData?.email}</DescriptionsItem>
                                <DescriptionsItem label={t("Mobile Number")}>{piResearcherProfileData?.mobile_number}</DescriptionsItem>
                            </Descriptions>

                            <h3 className='fw-bold blue-color-text'>Associate Investigator (AI): </h3>
                            <Descriptions
                                bordered
                                column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                            >
                                <DescriptionsItem label={t("Name")}>{aiResearcherProfileData?.name_en}</DescriptionsItem>
                                <DescriptionsItem label={t("Gender")}>{aiResearcherProfileData?.gender}</DescriptionsItem>
                                <DescriptionsItem label={t("Date of Birth")}>{DateTimeUtils.formatDate(aiResearcherProfileData?.dob)}</DescriptionsItem>
                                <DescriptionsItem label={t("Position")}>{aiResearcherProfileData?.position_en}</DescriptionsItem>
                                <DescriptionsItem label={t("Position held (since)")}>{aiResearcherProfileData?.position_held}</DescriptionsItem>
                                <DescriptionsItem label={t("E-mail")}>{aiResearcherProfileData?.email}</DescriptionsItem>
                                <DescriptionsItem label={t("Mobile Number")}>{aiResearcherProfileData?.mobile_number}</DescriptionsItem>
                            </Descriptions>

                            <div className='d-flex flex-column mt-3 text-center'>
                                {itemData.id && (
                                  <SpecialResearchGrantPreviewLoader applicationId={itemData.id} />
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    );
}
export default React.memo(DepartmentHeadRecommendationView);
